import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from "formik";
import * as Yup from "yup";

import messages from '../../../../assets/locale/messages';
import RadioButtonsGroup from '../../../../components/RadioButton';
import Modal from '../../../../components/Modal';
import { updateBookingRequest, editBookingServiceRequest, getRequestStatus } from '../../../../store/Bookings/actions';

const BELOW_REASON_KEYS = [
    'employee_did_not_show_up',
    'employee_is_late',
    'service_temporarily_unavailable',
    'customer_is_late',
    'customer_no_longer_wants_this_service',
];

const BookingFirstSessionCancelForm = ({ open, setOpen, buttonType, cancelService }) => {
    const dispatch = useDispatch();
    const lang = useSelector((state) => state.locale.lang);
    const { bookings, shared } = messages[lang];
    const bookingData = useSelector((state) => state.bookings.bookingDetails);
    const bookingCancelationReasons = useSelector((state) => state.bookings.bookingCancelationReasons);
    const bookingServiceCancelationReasons = useSelector((state) => state.bookings.bookingServiceCancelationReasons);

    const [reasonsList, setReasonsList] = useState([]);
    const [showRefundConfirmPopup, setShowRefundConfirmPopup] = useState(false);
    const [pendingReasonId, setPendingReasonId] = useState(null);

    const dispatchCancelBooking = (reasonId, applyRefund) => {
        if (buttonType === 'below') {
            dispatch(editBookingServiceRequest({
                id: cancelService?.id,
                data: {
                    appointment_branch_service_cancellation_reason_id: +reasonId,
                    apply_refund: applyRefund,
                },
            }));
        } else {
            const params = { apply_refund: applyRefund };
            if (reasonId) {
                params.appointment_cancellation_reason_id = +reasonId;
            }
            dispatch(updateBookingRequest({
                data: { id: bookingData?.id, params },
                requestType: 'cancel',
            }));
        }
        dispatch(getRequestStatus(false));
    };

    useEffect(() => {
        let mapped = [];

        if (buttonType === 'above') {
            mapped = (bookingCancelationReasons ?? []).map(r => ({
                id: r.id,
                label: (
                    <div className="fsize-20 fweight-400 text-gray">{r.reason}</div>
                ),
                value: String(r.id),
            }));
        } else {
            mapped = (bookingServiceCancelationReasons ?? [])
                .filter(r => BELOW_REASON_KEYS.includes(r.key))
                .sort((a, b) => BELOW_REASON_KEYS.indexOf(a.key) - BELOW_REASON_KEYS.indexOf(b.key))
                .map(r => ({
                    id: r.id,
                    label: (
                        <div className="fsize-20 fweight-400 text-gray">
                            {lang === 'en' ? r.reason_en : r.reason_ar}
                        </div>
                    ),
                    value: String(r.id),
                }));
        }

        setReasonsList(mapped);
    }, [lang, buttonType, bookingCancelationReasons, bookingServiceCancelationReasons]);

    const { handleSubmit, setFieldTouched, setFieldValue, values, touched, errors, resetForm } = useFormik({
        initialValues: { cancelationReason: '' },
        validationSchema: Yup.object({
            cancelationReason: Yup.string().required('Required'),
        }),
        onSubmit: ({ cancelationReason }) => {
            setPendingReasonId(cancelationReason);
            setOpen(false);
            setShowRefundConfirmPopup(true);
        },
    });

    const handleClose = () => {
        resetForm();
        setOpen(false);
    };

    return (
        <>
            <Modal
                open={open}
                handleClose={handleClose}
                title={<span className="fsize-26 fweight-400">{bookings.details.cancelBooking}</span>}
                contentClass="mb-3 pb-2 text-primary"
                content={
                    <div>
                        <div className="fsize-20 fweight-400 mb-3">
                            {bookings.details.bookingCancelationReasonLabel}*
                        </div>
                        <RadioButtonsGroup
                            name="cancelationReason"
                            labelId="cancelationReason"
                            value={values.cancelationReason}
                            list={reasonsList}
                            onChange={(e) => {
                                setFieldTouched("cancelationReason");
                                setFieldValue("cancelationReason", e.target.value);
                            }}
                        />
                    </div>
                }
                confirmBtnTxt={shared.submit}
                cancelBtnTxt={shared.cancel}
                isContentText={false}
                handleConfirm={() => handleSubmit()}
                handleCancel={handleClose}
                maxWidth="sm"
                actionsClass=" px-4"
                confirmBtnClass="btn-secondary fsize-14 text-white py-2 px-5"
                cancelBtnClass="fsize-14 py-2 text-secondary px-5"
                disableActionBtn={!!(!touched["cancelationReason"] || errors["cancelationReason"])}
            />

            <Modal
                open={showRefundConfirmPopup}
                handleClose={() => setShowRefundConfirmPopup(false)}
                title={<span className="fsize-26 fweight-400">{bookings.details.cancelBooking}</span>}
                contentClass="mb-3 pb-2 text-primary"
                content={
                    <span className="fsize-20 fweight-400">
                        {bookings.details.multiSessionCancelWholeAppointmentConfirm}
                    </span>
                }
                confirmBtnTxt={bookings.details.yesRefund}
                cancelBtnTxt={shared.no}
                isContentText={false}
                handleConfirm={() => {
                    setShowRefundConfirmPopup(false);
                    dispatchCancelBooking(pendingReasonId, true);
                }}
                handleCancel={() => {
                    setShowRefundConfirmPopup(false);
                    dispatchCancelBooking(pendingReasonId, false);
                }}
                maxWidth="sm"
                actionsClass=" px-4"
                confirmBtnClass="btn-secondary fsize-14 text-white py-2 px-5"
                cancelBtnClass="fsize-14 py-2 text-secondary px-5"
            />
        </>
    );
};

export default BookingFirstSessionCancelForm;
